import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { PasswordInput } from "@/components/PasswordInput";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { Switch } from "@/components/Switch";
import { userManagementApi, roleManagementApi, tenantApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { validatePassword } from "@/utils/passwordPolicy";

export interface NewUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const NewUserModal: React.FC<NewUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const activeCompanyId = localStorage.getItem("vigepra_active_company");
    const isMainCompany = !activeCompanyId || activeCompanyId === "main";

    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [needsPasswordChange, setNeedsPasswordChange] =
        useState<boolean>(true);

    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);

    const [roles, setRoles] = useState<
        { id: number; name: string; originalTag?: string }[]
    >([]);
    const [companies, setCompanies] = useState<{ id: number; name: string }[]>(
        []
    );
    const [loading, setLoading] = useState<boolean>(false);

    const [nameError, setNameError] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);

    const nameRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setName("");
            setEmail("");
            setPassword("Vito123!");
            setNeedsPasswordChange(true);

            setSelectedRoles([]);
            setSelectedCompanies([]);
            setNameError(false);
            setEmailError(false);
            setPasswordError(false);

            const fetchData = async (): Promise<void> => {
                try {
                    if (!isMainCompany) {
                        const rolesRes = await roleManagementApi.getAll();
                        if (rolesRes.result) {
                            setRoles(
                                rolesRes.data.map(
                                    (r: {
                                        id: number;
                                        name: string;
                                        tag?: string;
                                    }) => {
                                        const tagKey = `roles.tags.${(r.tag || r.name).toLowerCase()}`;
                                        const translated = t(tagKey);
                                        const displayName =
                                            translated !== tagKey
                                                ? translated
                                                : r.name;
                                        return {
                                            id: r.id,
                                            name: displayName,
                                            originalTag: r.tag,
                                        };
                                    }
                                )
                            );
                        }
                    } else {
                        const companiesRes = await tenantApi.getAll();
                        if (
                            companiesRes.result &&
                            companiesRes.data.companies
                        ) {
                            setCompanies(
                                companiesRes.data.companies.map(
                                    (c: { id: number; name?: string }) => ({
                                        id: c.id,
                                        name: c.name || String(c.id),
                                    })
                                )
                            );
                        }
                    }
                } catch {
                    // ignore error
                }
            };
            fetchData();
        }
    }, [isOpen, isMainCompany, t]);

    const handleSubmit = async () => {
        const missingName = !name.trim();
        const missingEmail = !email.trim();
        const missingPassword = !password;

        if (missingName || missingEmail || missingPassword) {
            setNameError(missingName);
            setEmailError(missingEmail);
            setPasswordError(missingPassword);

            showError(t("developments.modal_new.validation_required"));

            const firstMissing = missingName
                ? nameRef
                : missingEmail
                  ? emailRef
                  : passwordRef;
            firstMissing.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            setTimeout(() => {
                setNameError(false);
                setEmailError(false);
                setPasswordError(false);
            }, 3000);
            return;
        }

        const passwordErrorKey = validatePassword(password);
        if (passwordErrorKey) {
            setPasswordError(true);
            showError(t(passwordErrorKey));

            passwordRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            setTimeout(() => setPasswordError(false), 3000);
            return;
        }

        setLoading(true);
        try {
            const payload: {
                name: string;
                email: string;
                password: string;
                needs_password_change: boolean;
                roles?: number[];
                companies?: number[];
            } = {
                name,
                email,
                password,
                needs_password_change: needsPasswordChange,
            };

            if (!isMainCompany) {
                payload.roles = selectedRoles;
            } else {
                payload.companies = selectedCompanies;
            }

            await userManagementApi.create(payload);
            showSuccess(t("users.created"));
            onSuccess();
            onClose();
        } catch (error: unknown) {
            const err = error as { message?: string };
            showError(err.message || t("users.error_create"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("users.new_title")}
            onSubmit={handleSubmit}
            submitLabel={t("common.save")}
            submittingLabel={t("common.saving")}
            cancelLabel={t("common.cancel")}
            isSubmitting={loading}
            maxWidth="md"
        >
            <div className="space-y-4 p-4">
                <div ref={nameRef}>
                    <Input
                        label={t("users.name")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (nameError) setNameError(false);
                        }}
                        required
                        className={
                            nameError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div ref={emailRef}>
                    <Input
                        label={t("users.email")}
                        type="email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError(false);
                        }}
                        required
                        className={
                            emailError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div ref={passwordRef}>
                    <PasswordInput
                        label={t("users.password")}
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            if (passwordError) setPasswordError(false);
                        }}
                        required
                        defaultShowPassword={true}
                        className={
                            passwordError
                                ? "animate-error-blink rounded-xl"
                                : ""
                        }
                    />
                </div>

                <div>
                    <Switch
                        checked={needsPasswordChange}
                        onChange={setNeedsPasswordChange}
                        label={t("users.temporary_password")}
                        description={t("users.temporary_password_desc")}
                    />
                </div>

                {!isMainCompany && (
                    <SearchableMultiSelect
                        className="mb-4"
                        label={t("users.roles")}
                        helperText={t("users.roles_company_hint")}
                        options={roles}
                        value={selectedRoles}
                        onChange={setSelectedRoles}
                        placeholder={t("common.search")}
                    />
                )}

                {isMainCompany && (
                    <SearchableMultiSelect
                        label={t("companies.title")}
                        options={companies}
                        value={selectedCompanies}
                        onChange={setSelectedCompanies}
                        placeholder={t("common.search")}
                    />
                )}
            </div>
        </Modal>
    );
};
